import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, Send, Trash2 } from 'lucide-react';
import { streamSse } from './sse';
import { providerLabel } from './ProviderToggle';
import { AiMarkdown } from './AiMarkdown';

const API = process.env.REACT_APP_BACKEND_URL;
const opts = { withCredentials: true };

export const CaseAssistant = ({ caseId, provider }) => {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadSessions = async () => {
    const { data } = await axios.get(`${API}/api/cases/${caseId}/ai/sessions`, opts);
    setSessions(data);
  };

  useEffect(() => { loadSessions().catch(() => {}); }, [caseId]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [messages]);

  const openSession = async (id) => {
    setSessionId(id);
    const { data } = await axios.get(`${API}/api/ai/sessions/${id}/messages`, opts);
    setMessages(data);
  };

  const removeSession = async (id) => {
    await axios.delete(`${API}/api/ai/sessions/${id}`, opts);
    if (id === sessionId) { setSessionId(null); setMessages([]); }
    loadSessions();
  };

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: 'user', content: text }, { id: 'pending', role: 'assistant', content: '', provider }]);
    try {
      await streamSse(`/api/cases/${caseId}/ai/chat`, { message: text, provider, session_id: sessionId }, (evt) => {
        if (evt.session_id && !sessionId) setSessionId(evt.session_id);
        if (evt.t) setMessages((m) => m.map((x) => (x.id === 'pending' ? { ...x, content: x.content + evt.t } : x)));
      });
      setMessages((m) => m.map((x) => (x.id === 'pending' ? { ...x, id: `a-${Date.now()}` } : x)));
      loadSessions();
    } catch (err) {
      toast.error(err.message);
      setMessages((m) => m.filter((x) => x.id !== 'pending'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid md:grid-cols-[200px_1fr] gap-4">
      <aside className="space-y-2" data-testid="ai-chat-sessions">
        <button onClick={() => { setSessionId(null); setMessages([]); }} disabled={sending} data-testid="ai-chat-new-btn"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-dashed border-purple-300 text-purple-700 hover:bg-purple-50 transition-colors disabled:opacity-50">
          <Plus className="w-4 h-4" /> New chat
        </button>
        {sessions.map((s) => (
          <div key={s.id} className={`group flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm ${s.id === sessionId ? 'bg-purple-100' : 'hover:bg-gray-100'}`}>
            <button onClick={() => openSession(s.id)} disabled={sending} className="flex-1 text-left truncate normal-case disabled:opacity-50" data-testid={`ai-chat-session-${s.id}`}>{s.title}</button>
            <button onClick={() => removeSession(s.id)} disabled={sending} aria-label="Delete chat" data-testid={`ai-chat-delete-${s.id}`} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </aside>
      <div className="flex flex-col min-h-[360px]">
        <div className="flex-1 space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="ai-chat-messages">
          {messages.length === 0 && <p className="text-sm text-gray-500">Ask anything about this case. The assistant reads the case details and your saved evidence analyses.</p>}
          {messages.map((m) => (
            <div key={m.id} className={`rounded-lg p-3 ${m.role === 'user' ? 'bg-blue-600 text-white ml-10' : 'bg-gray-50 mr-10'}`} data-testid={`ai-chat-msg-${m.role}`}>
              {m.role === 'user' ? <p className="text-sm whitespace-pre-wrap normal-case">{m.content}</p> : (
                <>
                  <span className="text-[10px] font-semibold text-purple-600">{providerLabel(m.provider)}</span>
                  {m.content ? <AiMarkdown text={m.content} /> : <p className="text-sm text-gray-400 animate-pulse">Thinking...</p>}
                </>
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="mt-3 flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="e.g. Which dates conflict across the evidence?" data-testid="ai-chat-input"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm normal-case focus:outline-none focus:ring-2 focus:ring-purple-500" />
          <button type="submit" disabled={sending || !input.trim()} data-testid="ai-chat-send-btn"
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
};
